// Sentry — client-side init.
// This is the Next.js 15+ instrumentation pattern; there is intentionally
// NO sibling `sentry.client.config.ts` (the legacy file would double-init
// Sentry on every page load and burn through the free-tier event quota).
//
// Sample-rate strategy (free Developer plan friendly):
//   - tracesSampleRate         5% in prod, 100% in dev
//   - replaysSessionSampleRate 0% — never record idle sessions
//   - replaysOnErrorSampleRate 100% — always record sessions that error
//   - enabled                  prod only — keep dev noise out of Sentry
//   - sendDefaultPii           false — smaller payloads + privacy
//
// Replay loading strategy:
//   The replay integration is ~50 KB of extra JS we don't need at first
//   paint. We register it via `addIntegration` only after the page has
//   gone idle (or after the first error fires), so Lighthouse's
//   "Reduce unused JavaScript" + TBT metrics stop counting it against
//   first-paint cost. Errors that occur BEFORE replay loads still
//   surface in Sentry — they just don't include a video replay.

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/admin/emails/:id\",\"regex\":\"^/admin/emails/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/dashboard/datasets/:id\",\"regex\":\"^/dashboard/datasets/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/dpa\"},{\"path\":\"/privacy\"},{\"path\":\"/terms\"},{\"path\":\"/admin\"},{\"path\":\"/admin/analytics\"},{\"path\":\"/admin/emails\"},{\"path\":\"/admin/ops\"},{\"path\":\"/admin/status\"},{\"path\":\"/admin/users\"},{\"path\":\"/admin-unlock\"},{\"path\":\"/auth/reset-password\"},{\"path\":\"/changelog\"},{\"path\":\"/checkout\"},{\"path\":\"/dashboard\"},{\"path\":\"/dashboard/billing\"},{\"path\":\"/dashboard/compare\"},{\"path\":\"/dashboard/datasets\"},{\"path\":\"/dashboard/jobs\"},{\"path\":\"/dashboard/mapper\"},{\"path\":\"/dashboard/paste\"},{\"path\":\"/dashboard/settings\"},{\"path\":\"/dashboard/trash\"},{\"path\":\"/docs\"},{\"path\":\"/docs/api\"},{\"path\":\"/login\"},{\"path\":\"/preview/aperture\"},{\"path\":\"/preview/aperture/try\"},{\"path\":\"/preview/aperture-light\"},{\"path\":\"/preview/atelier\"},{\"path\":\"/preview/bone\"},{\"path\":\"/preview/cleaning-activity\"},{\"path\":\"/preview/cosmic\"},{\"path\":\"/preview/emails\"},{\"path\":\"/preview/emerald\"},{\"path\":\"/preview/horizon\"},{\"path\":\"/preview/issue\"},{\"path\":\"/preview/legacy\"},{\"path\":\"/preview/platform\"},{\"path\":\"/preview/post-office\"},{\"path\":\"/preview/precision\"},{\"path\":\"/preview/prism\"},{\"path\":\"/preview/showcase\"},{\"path\":\"/preview/spectrum\"},{\"path\":\"/preview/storm\"},{\"path\":\"/preview/terminal\"},{\"path\":\"/preview/toasts\"},{\"path\":\"/pricing\"},{\"path\":\"/sentry-example-page\"},{\"path\":\"/status\"},{\"path\":\"/try\"},{\"path\":\"/unsubscribe\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.1.6";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import * as Sentry from "@sentry/nextjs"

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

  enabled: process.env.NODE_ENV === "production" && process.env.NEXT_PUBLIC_E2E !== "1",

  // No replay here — added later in the idle callback below.
  integrations: [],

  tracesSampleRate:         process.env.NODE_ENV === "production" ? 0.05 : 1.0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  enableLogs:     true,
  sendDefaultPii: false,

  // Known browser noise — none of it is actionable for us.
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    "Non-Error promise rejection captured",
    /^Network Error/,
    /^Load failed/,

    // Supabase SSR client uses the Web Locks API to coordinate session
    // refresh across tabs. When a user has multiple tabs open, the
    // newer tab deliberately "steals" the lock from the older one to
    // avoid stale-token races. The older tab raises AbortError /
    // "Lock was stolen" / "Lock broken with steal option" / "Lock was
    // released because another request stole it" — all expected,
    // self-healing, no user impact. Filtering before the launch so a
    // multi-tab HN visitor doesn't inflate the issue counter for what
    // is in fact normal Supabase auth-client behavior.
    /Lock .* was released because another request stole it/,
    /Lock .* broken by another request with the 'steal' option/,
    /Lock was stolen by another request/,
  ],
})

// Defer replay until the browser is idle (or 5s in, whichever comes
// first on browsers without requestIdleCallback). 5s is a safe upper
// bound — by then LCP / TBT have settled and adding ~50 KB of replay
// JS doesn't hurt the Lighthouse score.
if (typeof window !== "undefined" && process.env.NODE_ENV === "production" && process.env.NEXT_PUBLIC_E2E !== "1") {
  const loadReplay = async () => {
    try {
      const replay = Sentry.replayIntegration({
        maskAllText:   true,
        blockAllMedia: false,
      })
      Sentry.addIntegration(replay)
    } catch {
      /* swallow — replay is best-effort, we still capture errors without it */
    }
  }
  if ("requestIdleCallback" in window) {
    ;(window as any).requestIdleCallback(loadReplay, { timeout: 5000 })
  } else {
    setTimeout(loadReplay, 5000)
  }
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart
